import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  useTheme,
  alpha,
  Chip,
  Stack,
  Avatar,
  CircularProgress,
  Divider,
  Tooltip
} from '@mui/material';
import RepeatIcon from '@mui/icons-material/Repeat';
import PlaceIcon from '@mui/icons-material/Place';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LaunchIcon from '@mui/icons-material/Launch';
import { envisFindingsApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import DefectProgressionModal from './DefectProgressionModal';

// Always pothole — persistence tracking is meaningful for structural defects, not transient debris
const SECTION_CONFIG = {
  title: 'Persistent Potholes',
  subtitle: 'Unresolved potholes re-detected across multiple patrols',
  emptyText: 'No persistent potholes found',
  emptySubtext: 'No unresolved potholes have been re-detected on multiple patrol days',
  defectType: 'pothole',
  cardColor: (theme) => theme.palette.error.main,
};

const PersistentDefectsSection = ({ user }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isAuthenticated, getUserOrganization } = useAuth();
  const config = SECTION_CONFIG;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDefect, setSelectedDefect] = useState(null);

  const userOrganization = getUserOrganization();
  const organizationId = userOrganization?.id;

  useEffect(() => {
    const fetchData = async () => {
      if (!isAuthenticated || !organizationId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const params = {
          defect_type: config.defectType,
        };

        if (user?.permissionScope === 'network_section') {
          const networkIds = user.accessible_networks || [];
          const sectionIds = user.accessible_sections || [];

          if (networkIds.length > 0) params.network_ids = networkIds.join(',');
          if (sectionIds.length > 0) params.section_ids = sectionIds.join(',');

          if (!networkIds.length && !sectionIds.length) {
            setData([]);
            setLoading(false);
            return;
          }
        }

        const response = await envisFindingsApi.getPersistentDefects(params);
        setData(response.data.data || []);
      } catch (err) {
        console.error(`Error fetching persistent defects:`, err);
        setError('Failed to load persistent defects');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isAuthenticated, organizationId, user]);

  const cardColor = config.cardColor(theme);

  const cardStyle = {
    p: 0,
    borderRadius: 2,
    height: 'fit-content',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'relative',
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.05)',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 12px 30px rgba(0, 0, 0, 0.08)',
    }
  };

  if (loading) {
    return (
      <Paper sx={{ ...cardStyle, minHeight: '400px', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress size={40} sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          Loading persistent defect data...
        </Typography>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ ...cardStyle, minHeight: '400px', justifyContent: 'center', alignItems: 'center' }}>
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>{error}</Typography>
        <Typography variant="caption" color="text.secondary">Please try refreshing the page</Typography>
      </Paper>
    );
  }

  if (!isAuthenticated || !organizationId) {
    return (
      <Paper sx={{ ...cardStyle, minHeight: '400px', justifyContent: 'center', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {!isAuthenticated ? 'Authentication required' : 'No organization access'}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={cardStyle}>
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: alpha(cardColor, 0.15),
          bgcolor: alpha(cardColor, 0.03)
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar
            sx={{
              bgcolor: alpha(cardColor, 0.1),
              color: cardColor,
              width: 32,
              height: 32,
              mr: 1.5
            }}
          >
            <RepeatIcon sx={{ fontSize: 18 }} />
          </Avatar>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2, fontSize: '0.8rem' }}>
              {config.title}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
              {config.subtitle}
            </Typography>
          </Box>
        </Box>

        <Chip
          size="small"
          label={`${data.length} found`}
          sx={{
            height: 20,
            fontSize: '0.7rem',
            bgcolor: alpha(cardColor, 0.1),
            color: cardColor,
            fontWeight: 600
          }}
        />
      </Box>

      {/* Content */}
      <Box sx={{ p: 1.5, maxHeight: 380, overflow: 'auto' }}>
        {data.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <RepeatIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">{config.emptyText}</Typography>
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
              {config.emptySubtext}
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1}>
            {data.map((item, index) => {
              const location = [item.networkName, item.sectionName, item.chainageName]
                .filter(Boolean).join(' \u2022 ');

              return (
                <Box
                  key={item.defectId}
                  onClick={() => { setSelectedDefect(item); setModalOpen(true); }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1.5,
                    borderRadius: 1.5,
                    cursor: 'pointer',
                    border: '1px solid',
                    borderColor: index === 0 ? alpha(cardColor, 0.3) : 'divider',
                    bgcolor: index === 0 ? alpha(cardColor, 0.04) : 'transparent',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: alpha(cardColor, 0.08),
                      borderColor: alpha(cardColor, 0.4),
                    },
                  }}
                >
                  {/* Rank */}
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: index < 3 ? cardColor : 'text.secondary',
                      fontSize: '0.7rem',
                      minWidth: 16,
                      textAlign: 'center',
                    }}
                  >
                    #{index + 1}
                  </Typography>

                  {/* Info */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.7rem' }}>
                        D-{item.defectId}
                      </Typography>
                      <Chip
                        size="small"
                        label={`${item.distinctPatrolDays} patrols`}
                        icon={<RepeatIcon sx={{ fontSize: '10px !important' }} />}
                        sx={{
                          height: 18,
                          fontSize: '0.6rem',
                          fontWeight: 600,
                          bgcolor: alpha(cardColor, 0.1),
                          color: cardColor,
                          '& .MuiChip-icon': { color: cardColor },
                        }}
                      />
                      {item.daysPersisting > 30 && (
                        <Tooltip title={`Persisting for ${item.daysPersisting} days`}>
                          <WarningAmberIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                        </Tooltip>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PlaceIcon sx={{ fontSize: 11, color: 'text.disabled' }} />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontSize: '0.65rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {location || 'Unknown location'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Days persisting */}
                  <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.8rem', color: cardColor }}>
                      {item.daysPersisting}d
                    </Typography>
                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: '0.55rem', lineHeight: 1 }}>
                      persisting
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>

      <Divider />

      {/* Footer */}
      <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
          Confirmed defects re-detected on 2+ patrol days
        </Typography>
        <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem' }}>
          Click to view progression
        </Typography>
      </Box>

      {/* Defect Progression Modal */}
      <DefectProgressionModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setSelectedDefect(null); }}
        defectId={selectedDefect?.defectId}
        defectInfo={selectedDefect ? {
          id: selectedDefect.defectId,
          defectType: selectedDefect.defectType,
          networkName: selectedDefect.networkName,
          sectionName: selectedDefect.sectionName,
          chainageName: selectedDefect.chainageName,
        } : null}
      />
    </Paper>
  );
};

export default PersistentDefectsSection;
